import type { ReactNode } from 'react';

import { Button } from '#/components/Button/Button';
import { Divider } from '#/components/Divider/Divider';
import { Dropdown } from '#/components/Dropdown/Dropdown';
import { DropdownMenu } from '#/components/Dropdown/menus/Menu/DropdownMenu';
import { DropdownMenuItem } from '#/components/Dropdown/menus/Menu/Item/DropdownMenuItem';
import { Icon } from '#/components/Icon/Icon';
import { Stack } from '#/components/Stack/Stack';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';
import type { ListingType } from '#/types/ListingType';

import { ReportListingMenuItem } from './ReportListingMenuItem';
import { SaveOrgMenuItem } from './SaveOrgMenuItem';
import { ShareMenuItem } from './ShareMenuItem';

type Props = {
  listingId: string;
  listingType: ListingType;
  listingUrl: Record<string, string>;

  orgDetails: {
    orgId: string;
    orgName: string;
    orgType: ApiOrgType;
    orgSaved: boolean;
    onSaveOrg: () => Promise<void>;
    onUnsaveOrg: () => Promise<void>;
  } | null;

  markAsAppliedButton: ReactNode;
};

export function ListingActionsDropdown({
  listingId,
  listingType,
  listingUrl,
  orgDetails,
  markAsAppliedButton,
}: Props) {
  const { i18n } = useI18n();

  return (
    <Dropdown
      aria-label={i18n('View more actions')}
      qaId="listings-more-actions"
      trigger={
        <Button
          type="inert"
          qaId="listings-more-actions-toggle"
          variant="secondary"
        >
          <Icon name="3-dots" size={18} />
        </Button>
      }
    >
      <DropdownMenu width="180px" mobilePlacement="top-left">
        <Stack
          direction="column"
          divider={<Divider marginBottom={0} marginTop={0} />}
        >
          <DropdownMenuItem mobileOnly>
            <ShareMenuItem
              listingId={listingId}
              listingType={listingType}
              listingUrl={listingUrl}
              orgId={orgDetails?.orgId ?? null}
            />
          </DropdownMenuItem>

          {markAsAppliedButton}

          {orgDetails && (
            <DropdownMenuItem>
              <SaveOrgMenuItem
                saved={orgDetails.orgSaved}
                onSave={orgDetails.onSaveOrg}
                onUnsave={orgDetails.onUnsaveOrg}
              />
            </DropdownMenuItem>
          )}

          <DropdownMenuItem>
            <ReportListingMenuItem
              listingId={listingId}
              listingType={listingType}
            />
          </DropdownMenuItem>
        </Stack>
      </DropdownMenu>
    </Dropdown>
  );
}
